import {
    CapabilityKey,
    CapabilityReason,
    PlanAccessLevel,
    gpCanUse,
    gpCanUseOnPlan,
} from './capabilities';
import { isUngatedFalseCapability } from './capability-catalogue';
import { isCapabilityGatingEnabled } from './feature-flags';

import type * as ng from 'angular';

/**
 * Asking the gate its two questions from inside the browser app, where the
 * answers depend on services that may or may not be registered.
 *
 * Shared by the `gp-capability` / `gp-if-withheld` directives and by
 * `gpCapabilityUpsell`, which must reach the SAME verdict: the directive
 * decides whether a control renders, the upsell service decides what happens
 * when it is used, and a control that renders live but whose action refuses —
 * or the reverse — is worse than either gate on its own.
 *
 * Everything here resolves through the injector rather than importing:
 * `gpPlanContent` and `gpPlanAccess` are registered on the workspace module,
 * and app-common must not depend on app-workspace. app-home and the reports app
 * legitimately have no plan model.
 */

/**
 * Does the gate act on `key` at all right now?
 *
 * `CapabilityGating` is the master switch for the TIER model, and it is off
 * until V2 ships. An ungated-false capability is not part of that model — it
 * is hidden from everybody the day it lands, per client, so waiting for the
 * master switch would mean waiting forever (issue #1066).
 *
 * Both halves agree on this because the server does the same thing: with
 * gating off it resolves every capability to its declared ungated value, which
 * is false for exactly these keys, so the snapshot the browser reads already
 * says no.
 */
export function capabilityGateApplies(key: CapabilityKey): boolean {
    return isCapabilityGatingEnabled() || isUngatedFalseCapability(key);
}

/**
 * The verdict reason for `key`, narrowed by plan access when a plan is open.
 *
 * Falls back to the user-scoped check off the workspace — app-home has no plan,
 * and `gpCanUseOnPlan` can only ever SUBTRACT, so skipping it there withholds
 * nothing that should have been withheld.
 *
 * The level is never read from markup or passed by a caller. It is a fact about
 * the session, not about the call site, and an argument would be an untyped
 * number — the exact path PlanAccessLevel's boxing exists to close.
 *
 * Plan narrowing is SKIPPED while gating is off, matching
 * `CapabilityResolver.ResolveUngated`, which documents why: the plan
 * requirement stays on the definition, so a caller that narrows anyway takes
 * the Add-collaborator button off an entitled Edit user while gating is
 * supposedly off.
 */
export function capabilityReasonFor(
    $injector: ng.auto.IInjectorService, key: CapabilityKey): CapabilityReason {

    const level = isCapabilityGatingEnabled() ? planAccessLevel($injector) : null;
    return level ? gpCanUseOnPlan(key, level).reason : gpCanUse(key).reason;
}

function planAccessLevel($injector: ng.auto.IInjectorService): PlanAccessLevel | null {
    if (!$injector.has('gpPlanAccess')) {
        return null;
    }

    try {
        return $injector.get<{ currentLevel(): PlanAccessLevel | null }>('gpPlanAccess').currentLevel();
    } catch {
        return null;
    }
}

/**
 * `isInPlan`, or false when nothing can answer it.
 *
 * False is the right answer with no plan open — with no plan there is no
 * existing content to preserve.
 */
export function planContainsCapability(
    $injector: ng.auto.IInjectorService, key: CapabilityKey): boolean {

    if (!$injector.has('gpPlanContent')) {
        return false;
    }

    try {
        return !!$injector.get<{ planContains(k: string): boolean }>('gpPlanContent').planContains(key);
    } catch {
        return false;
    }
}
